<template>
  <div class="viewStats"
    v-loading="loading">
    <div class="titleBar">
      <span class="title">VIEWS</span>

      <div class="tabSelectorWrapper">
        <span class="label">View By</span>

        <button @mouseup="tab = 'engagement'"
          :class="tab === 'engagement' ? 'active': ''">
          ENGAGEMENT
        </button>

        <button @mouseup="tab = 'referrer'"
          :class="tab === 'referrer' ? 'active' : ''">
          REFERRER
        </button>
      </div>
    </div>

    <div class="contentWrapper"
      v-if="tab === 'engagement'">
      <div class="iconWrapper">
        <img src="@assets/images/eye-icon.svg" alt="view icon">

        <span class="metricValue">{{ totalViews }}</span>

        <span class="metricTitle">Total Views</span>
      </div>

      <div class="dataWrapper">
        <div class="stat"
          v-for="(key, idx) in statKeys"
          :key="idx">
            <div class="label">{{ stats[key].label }}</div>

            <div class="value">{{ stats[key] | nebFormat }}</div>
          </div>
      </div>
    </div>

    <div class="contentWrapper"
      v-if="tab === 'referrer'">
      <div class="graphWrapper">
        <stat-popover-graph :data="graphData"
          :heroStat="heroStat"></stat-popover-graph>
      </div>

      <div class="tableWrapper">
        <stat-popover-table :columns="columns"
          :data="tableData"></stat-popover-table>
      </div>
    </div>
  </div>
</template>

<script>
import {
  includes,
  sortBy,
  keys
} from 'lodash'
import api from '@/api'
import endpoints from '@/api/endpoints'
import {
  formatPercent,
  abbreviateNumber
} from '@/helpers'

import StatPopoverTable from './StatPopoverTable.vue'
import StatPopoverGraph from './StatPopoverGraph.vue'

const columns = ['referrer', '% of views', 'brand avg']

const colorMap = {
  oo: '#359e19',
  internal: '#359e19',
  direct: '#59bc3d',
  social: '#7ed865',
  search: '#a9f893',
  other: '#d2ffc6',
  paid: '#d9f2d2'
}

const keyMap = {
  oo: 'Internal',
  internal: 'Internal',
  direct: 'Direct',
  social: 'Social',
  search: 'Search',
  other: 'Other',
  paid: 'Paid'
}

export default {
  name: 'viewStats',

  components: {
    StatPopoverTable,
    StatPopoverGraph
  },

  props: {
    docId: {
      type: String,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    metricDateStart: {
      type: String,
      default: '-10000'
    },
    metricDateEnd: {
      type: String,
      default: '0'
    }
  },

  data () {
    return {
      stats: {
        totalViews: 0,
        scrollPercentage: 0,
        scrollDepthPercentage: 0,
        byReferrer: {}
      },
      loading: true,
      statsLoaded: false,
      tab: 'engagement',
      columns
    }
  },

  computed: {
    statKeys () {
      return Object.keys(this.stats).filter(key => !includes(['totalViews', 'byReferrer'], key))
    },
    totalViews () {
      let {value = 0} = this.stats.totalViews

      if (value === null) {
        value = 0
      }

      return value.toLocaleString()
    },
    tableData () {
      const getBucketOrder = o => {
        const bucketOrderDict = {
          'internal': 0,
          'oo': 0,
          'direct': 1,
          'search': 3,
          'social': 2,
          'other': 4
        }
        return bucketOrderDict[o]
      }
      const { byReferrer } = this.stats

      const orderedBuckets = sortBy(keys(byReferrer), [getBucketOrder])

      return orderedBuckets.filter((key) => {
        return key !== 'totalIndex'
      }).map((key) => {
        return [
          { color: colorMap[key], type: 'string', value: keyMap[key] },
          { type: 'string', value: formatPercent(byReferrer[key].rate) },
          { type: 'string', value: formatPercent(byReferrer[key].brandRate) }
        ]
      })
    },
    graphData () {
      const { byReferrer } = this.stats

      return Object.keys(byReferrer)
        .map(key => {
          return {
            color: colorMap[key],
            label: keyMap[key],
            type: 'percent',
            value: byReferrer[key].rate.toFixed(2)
          }
        })
    },
    heroStat () {
      const { value } = this.stats.totalViews

      return {
        type: 'string',
        value: abbreviateNumber(value)
      }
    },
    footerData () {
      const { totalIndex } = this.stats.byReferrer
      return [
        { label: 'Total Views', type: 'number', value: this.stats.totalViews.value },
        { label: 'Article View Index', type: 'index', value: totalIndex }
      ]
    }
  },

  methods: {
    loadStats () {
      if (this.statsLoaded) {
        // We already have our stats. do not fire another request.
        return
      }

      this.loading = true
      this.getPageViewStats()
        .then(resp => {
          this.stats = Object.assign(this.stats, resp.data)

          this.loading = false
          this.statsLoaded = true
        })
    },
    getPageViewStats () {
      const metricDateRange = [
        this.metricDateStart,
        this.metricDateEnd
      ]

      const params = {
        brandId: this.brandId,
        metricDateRange
      }

      return api.get(endpoints.assets.viewAndScrollStats(this.docId), { params })
    },
    resetStats () {
      this.stats = {
        totalViews: null,
        articleViewIndex: null,
        scrollPercentage: null,
        scrollDepthPercentage: null
      }
      this.loading = true
      this.statsLoaded = false
    }
  },

  watch: {
    visible (newValue) {
      if (newValue) {
        this.loadStats()
      }
    },
    metricDateStart () { this.statsLoaded = false },
    metricDateEnd () { this.statsLoaded = false },
    docId () { this.statsLoaded = false }
  }
}
</script>

<style lang="scss" scoped>
.viewStats {
  box-sizing: border-box;
  overflow: hidden;
  height: 275px;
  width: 535px;

  .tabSelectorWrapper {
    .label {
      width: 37px;
      height: 11px;
      font-size: 10px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.1;
      letter-spacing: 0.2px;
      text-align: right;
      color: #5c5e62;
      margin-right: 5px;
    }

    button {
      border: solid 1px #cecece;
      background-color: #fafafa;
      color: #cecece;
      font-size: 10px;
      padding: 5px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.1;
      letter-spacing: 0.2px;
      cursor: pointer;

      &:not(:first-of-type) {
        margin-left: 5px;
      }

      &.active {
        background-color: #e8f4fc;
        border: solid 1px #74cfe7;
        color: #74cfe7;
      }
    }
  }

  .graphWrapper {
    margin-top: 20px;
    margin-left: 30px;
    margin-right: 35px;
  }

  .tableWrapper {
    margin-top: 16px;
  }

  .footer {
      border-top: solid 1px #cecece;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: space-evenly;

      .container {
        height: 25px;

        .label {
          font-size: 10px;
          text-transform: uppercase;
        }

        .value {
          font-weight: bold;
          font-style: 11px;
        }
      }
    }

  .titleBar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 25px 25px 0 25px;

    .title {
      line-height: 0.79;
      font-size: 14px;
      letter-spacing: 0.3px;
      color: #5c5e62;
    }
  }

  .contentWrapper {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    box-sizing: border-box;
    overflow: hidden;

    .iconWrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      img {
        padding-left: 65px;
        padding-right: 65px;
        height: 41px;
        margin-bottom: 20px;
        margin-top: 53px;
      }

      .metricValue {
        font-size: 20px;
        font-weight: 500;
        color: #5c5e62;
      }

      .metricTitle {
        font-size: 12px;
        font-weight: 300;
        color: 555555;
        margin-bottom: 53px;
      }
    }

    .dataWrapper {
      border-left: 1px solid #cecece;
      padding-left: 24px;
      padding-right: 35px;
      box-sizing: border-box;
      height: 150px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;

      .stat {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        .label {
          flex-basis: 75%;
          flex-grow: 1;
          white-space: nowrap;
          padding-right: 110px;
          font-size: 12px;
          color: #555555;
          line-height: 2.92;
          font-weight: 300;
        }

        .value {
          font-size: 14px;
          line-height: 2.5;
          color: #5c5e62;
          font-weight: bold;
          min-width: 50px;
          text-align: right;
        }
      }
    }
  }
}
</style>

